import { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import { logEvent } from "@/lib/analytics";

const AuthContext = createContext(null);

export const ADMIN_EMAIL = "pixelpy18@gmail.com";
export const SUPER_ADMIN_EMAIL = "kairox.1302@gmail.com";

const normalizeEmail = (email) => (email || "").trim().toLowerCase();
export const authErrorMessage = (error) => {
  const code = error?.code || "";
  if (code === "auth/unauthorized-domain") {
    return "Google login is blocked because this domain is not added in Firebase Auth authorized domains.";
  }
  if (code === "auth/operation-not-allowed") {
    return "Google login is not enabled in Firebase Authentication.";
  }
  if (code === "auth/popup-blocked") {
    return "Popup was blocked. Trying redirect sign-in...";
  }
  if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
    return "Google sign-in was cancelled.";
  }
  if (code === "auth/network-request-failed") {
    return "Network error. Please check your connection and try again.";
  }
  return error?.message?.replace("Firebase: ", "") || "Authentication failed";
};

const isAdminEmail = (email) => {
  const cleanEmail = normalizeEmail(email);
  return cleanEmail === ADMIN_EMAIL || cleanEmail === SUPER_ADMIN_EMAIL;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    let active = true;
    getRedirectResult(auth)
      .then(async (cred) => {
        if (!active || !cred?.user) return;
        await persistUser(cred.user);
        logEvent("login", { method: "google_redirect" });
      })
      .catch((error) => {
        console.error("Google redirect sign-in failed", error);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistUser = async (u, extra = {}) => {
    try {
      await setDoc(
        doc(db, "users", u.uid),
        {
          email: u.email,
          name: u.displayName || extra.name || "",
          photoURL: u.photoURL || "",
          isAdmin: isAdminEmail(u.email),
          isSuperAdmin: normalizeEmail(u.email) === SUPER_ADMIN_EMAIL,
          lastLoginAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          ...extra,
        },
        { merge: true }
      );
      if (!isAdminEmail(u.email)) {
        await setDoc(
          doc(db, "checkout_visitors", u.uid),
          {
            userId: u.uid,
            email: u.email,
            emailLower: normalizeEmail(u.email),
            firstName: (u.displayName || extra.name || "").split(" ")[0] || "",
            lastName: (u.displayName || extra.name || "").split(" ").slice(1).join(" "),
            source: "signup",
            status: "signed_up_no_paid_order",
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }
    } catch (e) {
      // ignore firestore permission errors
    }
  };

  const signup = async (email, password, name) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    if (name) await updateProfile(cred.user, { displayName: name });
    await persistUser(cred.user, { name });
    logEvent("signup", { method: "email" });
    return cred.user;
  };

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    await persistUser(cred.user);
    logEvent("login", { method: "email" });
    return cred.user;
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    try {
      const cred = await signInWithPopup(auth, provider);
      await persistUser(cred.user);
      logEvent("login", { method: "google" });
      return cred.user;
    } catch (error) {
      if (error?.code === "auth/popup-blocked") {
        await signInWithRedirect(auth, provider);
        return null;
      }
      throw error;
    }
  };

  // Admin flow: verify creds match, then sign in — auto-create the admin
  // account on the first successful attempt so the credentials always work.
  const loginAdmin = async (email, password) => {
    const cleanEmail = normalizeEmail(email);
    if (!isAdminEmail(cleanEmail)) {
      const err = new Error("Invalid admin credentials");
      err.code = "admin/invalid";
      throw err;
    }
    try {
      const cred = await signInWithEmailAndPassword(auth, cleanEmail, password);
      await persistUser(cred.user, { name: cleanEmail === SUPER_ADMIN_EMAIL ? "Super Admin" : "Admin", isAdmin: true, isSuperAdmin: cleanEmail === SUPER_ADMIN_EMAIL });
      logEvent("admin_login");
      return cred.user;
    } catch (err) {
      const code = err?.code || "";
      if (
        code === "auth/user-not-found" ||
        code === "auth/invalid-credential" ||
        code === "auth/invalid-login-credentials"
      ) {
        const cred = await createUserWithEmailAndPassword(auth, cleanEmail, password);
        await updateProfile(cred.user, { displayName: cleanEmail === SUPER_ADMIN_EMAIL ? "Super Admin" : "Admin" });
        await persistUser(cred.user, { name: cleanEmail === SUPER_ADMIN_EMAIL ? "Super Admin" : "Admin", isAdmin: true, isSuperAdmin: cleanEmail === SUPER_ADMIN_EMAIL });
        logEvent("admin_bootstrap");
        return cred.user;
      }
      throw err;
    }
  };

  const logout = () => signOut(auth);

  const isSuperAdmin = !!user && normalizeEmail(user.email) === SUPER_ADMIN_EMAIL;
  const isAdmin = !!user && isAdminEmail(user.email);

  return (
    <AuthContext.Provider
      value={{ user, loading, isAdmin, isSuperAdmin, signup, login, loginWithGoogle, loginAdmin, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
